import React, { useEffect, Suspense, lazy, useState } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { Navigation } from './components/Navigation';
import { Hero } from './components/Hero';
import { About } from './components/About';
import { ServicesInfo } from './components/ServicesInfo';
import { MenuSection } from './components/Menu';
import { OrderProvider } from './context/OrderContext';
import { SettingsProvider } from './context/SettingsContext';

const Gallery = lazy(() => import('./components/Gallery').then(mod => ({ default: mod.Gallery })));
const Reviews = lazy(() => import('./components/Reviews').then(mod => ({ default: mod.Reviews })));
const Delivery = lazy(() => import('./components/Delivery').then(mod => ({ default: mod.Delivery })));
const Footer = lazy(() => import('./components/Footer').then(mod => ({ default: mod.Footer })));
const FloatingOrderButton = lazy(() => import('./components/FloatingOrderButton').then(mod => ({ default: mod.FloatingOrderButton })));
const OrderModal = lazy(() => import('./components/OrderModal').then(mod => ({ default: mod.OrderModal })));
const CartDrawer = lazy(() => import('./components/CartDrawer').then(mod => ({ default: mod.CartDrawer })));
const CookieBanner = lazy(() => import('./components/CookieBanner').then(mod => ({ default: mod.CookieBanner })));

const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(mod => ({ default: mod.AdminDashboard })));
const LegalPages = lazy(() => import('./components/LegalPages').then(mod => ({ default: mod.LegalPages })));

function DeferredComponents() {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (!shouldRender) return null;

  return (
    <Suspense fallback={null}>
      <Gallery />
      <Reviews />
      <Delivery />
      <Footer />
      <FloatingOrderButton />
      <OrderModal />
      <CartDrawer />
      <CookieBanner />
    </Suspense>
  );
}

function LandingPage() {
  useEffect(() => {
    // Disable browser's default scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="font-sans text-gusto-dark bg-gusto-cream selection:bg-gusto-red/20 selection:text-gusto-red min-h-screen flex flex-col">
      <Navigation />
      <Hero />
      <About />
      <ServicesInfo />
      <MenuSection />
      <DeferredComponents />
    </div>
  );
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <ErrorBoundary>
      <SettingsProvider>
        <OrderProvider>
          {path === '/admin' ? (
            <Suspense fallback={<div className="min-h-screen flex flex-col items-center justify-center font-sans text-gusto-dark bg-gusto-cream">Зареждане...</div>}>
              <AdminDashboard />
            </Suspense>
          ) : path === '/privacy' ? (
            <Suspense fallback={<div className="min-h-screen flex flex-col items-center justify-center font-sans text-gusto-dark bg-gusto-cream">Зареждане...</div>}>
              <LegalPages title="Политика за поверителност" type="privacy" />
            </Suspense>
          ) : path === '/terms' ? (
            <Suspense fallback={<div className="min-h-screen flex flex-col items-center justify-center font-sans text-gusto-dark bg-gusto-cream">Зареждане...</div>}>
              <LegalPages title="Общи условия" type="terms" />
            </Suspense>
          ) : path === '/cookies' ? (
            <Suspense fallback={<div className="min-h-screen flex flex-col items-center justify-center font-sans text-gusto-dark bg-gusto-cream">Зареждане...</div>}>
              <LegalPages title="Политика за бисквитките" type="cookies" />
            </Suspense>
          ) : (
            <LandingPage />
          )}
        </OrderProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
}


